// @ts-nocheck
import React, { useMemo, useState, useCallback, useEffect } from 'react';
import {
  Box,
  Dialog,
  Grid,
  IconButton,
  Button,
  Autocomplete,
  TextField,
  Typography,
  DialogContent,
  DialogActions,
  DialogTitle,
  Card,
  CardContent,
} from '@mui/material';
import { object, string } from 'yup';
import CloseIcon from '@mui/icons-material/Close';
import PropTypes from 'prop-types';
import { useFormik } from 'formik';
import { useSelector } from 'react-redux';

import { ReactComponent as DropdownArrow } from '../../../../assets/images/DropdownArrow.svg';
import SwalToast from '../../swalToast';
import DialogTransition from '../../Transition/DialogTransition';
import { isValueEmpty } from '../../../../utils/Utils';
import CustomPopover from '../../CustomPopover';
import useEntity from '../../../../hooks/ModelInventory/useEntity';
import useTeam from '../../../../hooks/ModelInventory/useTeam';
import {
  EMPTY_ARRAY,
  ENABLE_GROUP_SELECTION_FOR_ROLES,
  NOOP,
} from '../../../../const/CommonConst';

const AssignUserModal = ({
  open,
  handleClose,
  attribute,
  entityData,
  usersList = EMPTY_ARRAY,
  teamUsersList = EMPTY_ARRAY,
  groupList = EMPTY_ARRAY,
  value,
  onSave,
  possibleRoles = EMPTY_ARRAY,
  getNextPossibleStates,
  getModelDetails,
  handleGetAttributesData,
  getCustomTeamDetails = NOOP,
}) => {
  const { updateModelEntity } = useEntity();
  const { updateUserDetails, addSingleTeamMember } = useTeam();
  const [isLoading, setIsLoading] = useState(false);
  const [filteredUserList, setFilteredUserList] = useState([]);
  const { profileData } = useSelector((state) => state.users);
  const { pluginConfig } = useSelector((state) => state?.tenant);

  const entityId = entityData?.entity_id;
  const entityType = entityData?.entity_type;

  /**
   * @function getFilteredGroupsByRole
   * @param {string} role - The role to filter groups by
   * @returns {Array} Filtered array of groups that match the role or have null role
   */
  const getFilteredGroupsByRole = useCallback(
    (role) => {
      if (pluginConfig?.HDFCUAM) {
        return (groupList || [])?.filter((group) => group?.role === role);
      }
      return (groupList || [])?.filter(
        (group) => group?.role === role || group?.role == null
      );
    },
    [groupList, pluginConfig]
  );

  const findTeamMemberByRole = useCallback(
    (role) => {
      return teamUsersList?.find((member) => member?.role === role);
    },
    [teamUsersList]
  );

  /**
   * @function getAutoSelectedGroupId
   * @param {string} role - The role to auto-select group for
   * @returns {{ label: string, value: string }} Group option if HDFCUAM match found, else empty
   */
  const getAutoSelectedGroupId = useCallback(
    (role) => {
      if (!role || !pluginConfig?.HDFCUAM) {
        return { label: '', value: '' };
      }
      const filteredGroups = getFilteredGroupsByRole(role);
      const matchingGroup = filteredGroups?.find(
        (group) => group?.role === role
      );
      return matchingGroup
        ? { label: matchingGroup?.groupName, value: matchingGroup?.id }
        : { label: '', value: '' };
    },
    [getFilteredGroupsByRole, pluginConfig]
  );

  const initialValues = useMemo(() => {
    if (!possibleRoles?.length) {
      return { groupId: { label: '', value: '' }, role: '', user: null };
    }

    // Find the first role that already exists in the team
    const existingRole = (possibleRoles || [])?.find((role) =>
      findTeamMemberByRole(role)
    );

    const selectedRole = existingRole || possibleRoles?.[0] || '';
    const teamMember = findTeamMemberByRole(selectedRole);

    const currentUser =
      usersList?.find((u) => u.user_id === value || u.username === value) ||
      null;

    if (teamMember) {
      // Role exists in team — prefill from team member data
      let initialGroupId;
      if (teamMember?.group) {
        const groupOption = groupList?.find(
          (g) => g?.groupName === teamMember?.group
        );
        initialGroupId = groupOption
          ? { label: teamMember?.group, value: groupOption?.id }
          : { label: teamMember?.group, value: teamMember?.group_id || '' };
      } else {
        // No group on team member — auto-select group by role if HDFCUAM
        initialGroupId = getAutoSelectedGroupId(selectedRole);
      }

      const matchedUser =
        usersList?.find((u) => u.username === teamMember?.user) || currentUser;

      return {
        groupId: initialGroupId,
        role: selectedRole,
        user: matchedUser,
      };
    }

    // Role does NOT exist in team — auto-select group if HDFCUAM
    const initialGroupId = getAutoSelectedGroupId(selectedRole);

    return {
      groupId: initialGroupId,
      role: selectedRole,
      user: currentUser,
    };
  }, [
    possibleRoles,
    findTeamMemberByRole,
    groupList,
    usersList,
    value,
    getAutoSelectedGroupId,
  ]);

  const formValidationSchema = object({
    role: string().required('Required.'),
    groupId: object().nullable(),
    user: object().nullable().required('Required'),
  });

  const formikForm = useFormik({
    initialValues,
    validationSchema: formValidationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      setIsLoading(true);
      try {
        // Update Attribute
        const attributePayload = {
          [attribute?.id]: values.user?.user_id || null,
          entity_id: entityId,
          entity_type: entityType,
        };
        await updateModelEntity(attributePayload);

        // Check if role already exists in team
        const memberExists = findTeamMemberByRole(values?.role);
        const updatedUser = values.user?.username || null;

        if (memberExists) {
          // Role exists in team — update existing team member
          const teamPayload = {
            entity_id: entityId,
            entity_type: entityType,
            role: values?.role,
            updated_group: null, // Group is only for filtering, never assigned directly
            updated_user: updatedUser,
          };
          await updateUserDetails(teamPayload);
        } else {
          // Role NOT in team — add new team member
          const teamMember = {};
          teamMember[values?.role] = {
            is_grp: false,
            name: updatedUser,
            sub_team_members: {},
          };
          await addSingleTeamMember({
            entity_id: entityId,
            entity_type: entityType,
            team: teamMember,
          });
        }

        SwalToast({
          icon: 'success',
          title: 'User assigned successfully.',
        });

        // Execute comprehensive refresh logic
        if (handleGetAttributesData) handleGetAttributesData();
        if (attribute?.attribute_origin === 'common' && getModelDetails) {
          getModelDetails();
        }
        if (getNextPossibleStates) getNextPossibleStates();
        if (getCustomTeamDetails)
          getCustomTeamDetails({
            entityType,
            entityId,
          });

        onSave();
        handleClose();
      } finally {
        setIsLoading(false);
      }
    },
  });

  const handleRoleChange = useCallback(
    (_event, newValue) => {
      formikForm.setFieldValue('role', newValue);

      // Check if the newly selected role exists in team
      const memberForRole = findTeamMemberByRole(newValue);
      if (memberForRole) {
        // Prefill with team member data
        let groupId;
        if (memberForRole?.group) {
          const groupOption = groupList?.find(
            (g) => g?.groupName === memberForRole?.group
          );
          groupId = groupOption
            ? { label: memberForRole?.group, value: groupOption?.id }
            : {
                label: memberForRole?.group,
                value: memberForRole?.group_id || '',
              };
        } else {
          // No group on team member — auto-select group by role if HDFCUAM
          groupId = getAutoSelectedGroupId(newValue);
        }
        formikForm.setFieldValue('groupId', groupId);

        const selectedUser = usersList.find(
          (u) => u.username === memberForRole?.user
        );
        formikForm.setFieldValue('user', selectedUser || null);
      } else {
        // Role not in team — auto-select group if HDFCUAM
        formikForm.setFieldValue('user', null);
        formikForm.setFieldValue('groupId', getAutoSelectedGroupId(newValue));
      }
    },
    [
      formikForm,
      findTeamMemberByRole,
      groupList,
      usersList,
      getAutoSelectedGroupId,
    ]
  );

  const filteredGroupList = useMemo(() => {
    return getFilteredGroupsByRole(formikForm.values.role);
  }, [getFilteredGroupsByRole, formikForm.values.role]);

  const groupOptions = useMemo(() => {
    return (
      filteredGroupList?.map((group) => ({
        label: group?.groupName,
        value: group?.id,
      })) || []
    );
  }, [filteredGroupList]);
  // Disable group if auto-populated AND role is NOT allowed
  const isGroupAutoPopulated = useMemo(() => {
    const isAutoPopulated = Boolean(initialValues?.groupId?.value);
    const isRoleAllowed = ENABLE_GROUP_SELECTION_FOR_ROLES.includes(
      profileData?.role
    );
    return isAutoPopulated && !isRoleAllowed && Boolean(pluginConfig?.HDFCUAM);
  }, [initialValues?.groupId?.value, pluginConfig?.HDFCUAM, profileData?.role]);

  useEffect(() => {
    if (isValueEmpty(formikForm?.values?.groupId?.value)) {
      setFilteredUserList(usersList || []);
    } else {
      const filteredUserListOnGroups = usersList?.filter((user) =>
        user?.groups?.find(
          (group) => group?.groupId === formikForm?.values?.groupId?.value
        )
      );
      setFilteredUserList(filteredUserListOnGroups || []);
    }
  }, [formikForm?.values?.groupId, usersList]);

  // User options for Autocomplete
  const userOptions = useMemo(() => {
    return (filteredUserList || [])?.map((usr) => usr?.username);
  }, [filteredUserList]);

  const handleCloseAndResetForm = useCallback(() => {
    handleClose();
    formikForm?.handleReset();
  }, [handleClose, formikForm]);

  const handleRoleOptionLabel = useCallback((option) => {
    return option?.label || option || '';
  }, []);

  const handleGroupChange = useCallback(
    (_, newValue) => {
      formikForm.setFieldValue('groupId', newValue || { label: '', value: '' });
      formikForm.setFieldValue('user', null);
    },
    [formikForm]
  );

  const handleGetGroupOptionLabel = useCallback((option) => {
    return option?.label || '';
  }, []);

  const handleUserAssignmentChange = useCallback(
    (_, newValue) => {
      const selectedUser = filteredUserList.find(
        (u) => u.username === newValue
      );
      formikForm.setFieldValue('user', selectedUser);
    },
    [formikForm, filteredUserList]
  );

  const renderRoleInput = useCallback(
    (params) => (
      <TextField
        {...params}
        label="Role"
        required
        placeholder="Select Role"
        variant="outlined"
        spellCheck={true}
        error={formikForm.touched.role && Boolean(formikForm.errors.role)}
        helperText={formikForm.touched.role && formikForm.errors.role}
        InputLabelProps={{ shrink: true }}
      />
    ),
    [formikForm.touched.role, formikForm.errors.role]
  );

  const renderGroupInput = useCallback(
    (params) => (
      <TextField
        {...params}
        label="Group"
        placeholder="Select Group"
        variant="outlined"
        spellCheck={true}
        InputLabelProps={{ shrink: true }}
        error={formikForm.touched.groupId && Boolean(formikForm.errors.groupId)}
        helperText={formikForm.touched.groupId && formikForm.errors.groupId}
      />
    ),
    [formikForm.touched.groupId, formikForm.errors.groupId]
  );

  const renderUserInput = useCallback(
    (params) => (
      <TextField
        {...params}
        label="User"
        required
        placeholder="Select User"
        variant="outlined"
        spellCheck={true}
        InputLabelProps={{ shrink: true }}
        error={formikForm.touched.user && Boolean(formikForm.errors.user)}
        helperText={formikForm.touched.user && formikForm.errors.user}
      />
    ),
    [formikForm.touched.user, formikForm.errors.user]
  );

  return (
    <Dialog
      maxWidth="xs"
      fullWidth
      open={open}
      onClose={handleCloseAndResetForm}
      TransitionComponent={DialogTransition}
    >
      <DialogTitle>
        <Box component="span">Assign User</Box>
      </DialogTitle>

      <IconButton
        color="primary"
        onClick={handleCloseAndResetForm}
        sx={{ position: 'absolute', right: 16, top: 8 }}
      >
        <CloseIcon />
      </IconButton>

      <DialogContent dividers>
        <Grid container rowSpacing={2}>
          <Grid item xs={12}>
            <Typography variant="body4">
              <strong>Note:</strong>&nbsp;
              <i>
                Select a <strong>User</strong>.
              </i>
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Autocomplete
              options={possibleRoles || []}
              value={formikForm?.values?.role || ''}
              getOptionLabel={handleRoleOptionLabel}
              onChange={handleRoleChange}
              renderInput={renderRoleInput}
              popupIcon={<DropdownArrow />}
              PopperComponent={CustomPopover}
              noOptionsText="No role available."
            />
          </Grid>

          <Grid item xs={12}>
            <Card variant="outlined" sx={{ borderRadius: 2 }}>
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="body1" color="text.secondary" mb={1}>
                      Assign To:
                    </Typography>
                  </Grid>

                  <Grid item xs={12}>
                    <Autocomplete
                      disabled={isGroupAutoPopulated}
                      options={groupOptions}
                      value={formikForm.values.groupId}
                      getOptionLabel={handleGetGroupOptionLabel}
                      onChange={handleGroupChange}
                      renderInput={renderGroupInput}
                      popupIcon={<DropdownArrow />}
                      PopperComponent={CustomPopover}
                      noOptionsText="No group present."
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Autocomplete
                      options={userOptions}
                      value={formikForm.values.user?.username || ''}
                      onChange={handleUserAssignmentChange}
                      renderInput={renderUserInput}
                      popupIcon={<DropdownArrow />}
                      PopperComponent={CustomPopover}
                      noOptionsText="No user present."
                      disableClearable={!formikForm?.values?.user}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button
          variant="outlined"
          onClick={handleCloseAndResetForm}
          disabled={isLoading}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={formikForm.handleSubmit}
          disabled={isLoading}
        >
          {isLoading ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

AssignUserModal.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  attribute: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    attribute_origin: PropTypes.string,
  }).isRequired,
  entityData: PropTypes.shape({
    entity_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    entity_type: PropTypes.string,
  }).isRequired,
  usersList: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  teamUsersList: PropTypes.arrayOf(PropTypes.shape({})),
  groupList: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onSave: PropTypes.func.isRequired,
  possibleRoles: PropTypes.arrayOf(PropTypes.string),
  getNextPossibleStates: PropTypes.func,
  getModelDetails: PropTypes.func,
  handleGetAttributesData: PropTypes.func,
  getCustomTeamDetails: PropTypes.func,
};

export default AssignUserModal;
